import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to detect errors from the embed script and handle script loading
 * @param tenantId - The tenant ID to use for the embed script
 * @returns The error state if any error occurred
 */
export function useEmbedErrorDetection(tenantId: string | undefined, redirect: boolean = true) {

  const [error, setError] = useState<Error | null>(null);
  const originalConsoleError = useRef<typeof console.error | null>(null);
  const errorCheckTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear error when redirect is disabled
  useEffect(() => {
    if (!redirect) {
      setError(null);
    }
  }, [redirect]);

  useEffect(() => {
    // Error handler for custom embed errors (most reliable)
    const handleEmbedError = (event: CustomEvent) => {
      console.error('[Embed Error Detection] Custom embed error:', event.detail);
      if (redirect) {
        const { error, message } = event.detail;
        setError(error instanceof Error ? error : new Error(message || 'Embed initialization failed'));
      }
    };

    // Error handler for script loading errors and global errors
    const handleError = (event: ErrorEvent) => {
      console.error('Error:', event.error);
      if (redirect) {
        setError(event.error || new Error('An error occurred'));
      }
    };

    // Error handler for unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);

      // Check if the rejection reason contains authentication or critical errors
      const reasonString = event.reason?.toString() || JSON.stringify(event.reason) || '';
      const reasonLower = reasonString.toLowerCase();

      const isCriticalError =
        reasonLower.includes('login failed') ||
        reasonLower.includes('apolloerror') ||
        reasonLower.includes('500') ||
        reasonLower.includes('internal server error') ||
        reasonLower.includes('context creation failed') ||
        reasonLower.includes('must be a string of 12 bytes') ||
        reasonLower.includes('must be a string of 24 hex characters') ||
        reasonLower.includes('failed to fetch license key') ||
        reasonLower.includes('missing publickey') ||
        reasonLower.includes('missing public key') ||
        reasonLower.includes('invalid response') ||
        reasonLower.includes('http error') ||
        reasonLower.includes('graphql errors');

      if (redirect && (isCriticalError || !event.reason)) {
        setError(event.reason instanceof Error
          ? event.reason
          : new Error(reasonString || 'Unhandled error occurred'));
      }
    };

    // Intercept console.error to catch embed script errors
    originalConsoleError.current = console.error;
    console.error = (...args: any[]) => {
      // Call original console.error
      if (originalConsoleError.current) {
        originalConsoleError.current.apply(console, args);
      }

      // Convert all args to string for pattern matching
      // Handle Error objects, strings, and other types
      const errorStrings = args.map(arg => {
        if (arg instanceof Error) {
          return arg.message + ' ' + (arg.stack || '');
        }
        if (typeof arg === 'object' && arg !== null) {
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      });

      const errorMessage = errorStrings.join(' ').toLowerCase();
      const fullErrorMessage = errorStrings.join(' ');

      // Check for embed-specific errors
      const isEmbedError =
        errorMessage.includes('[horizonshortsembed]') &&
        (errorMessage.includes('failed to fetch license key') ||
          errorMessage.includes('aborting initialization') ||
          errorMessage.includes('missing publickey') ||
          errorMessage.includes('missing public key') ||
          errorMessage.includes('invalid response'));

      // Check for authentication/login errors
      const isAuthError =
        errorMessage.includes('login failed') ||
        errorMessage.includes('apolloerror') ||
        errorMessage.includes('apollo error') ||
        errorMessage.includes('500') ||
        errorMessage.includes('internal server error') ||
        errorMessage.includes('context creation failed') ||
        errorMessage.includes('must be a string of 12 bytes') ||
        errorMessage.includes('must be a string of 24 hex characters') ||
        errorMessage.includes('http fetch failed');

      if (isEmbedError || isAuthError) {
        if (redirect) {
          // Try to extract the actual error object if available
          const errorObj = args.find(arg => arg instanceof Error);
          setError(errorObj || new Error(isEmbedError
            ? 'Embed initialization failed: ' + fullErrorMessage
            : 'Authentication failed: ' + fullErrorMessage));
        }
      }
    };

    // Add error event listeners
    window.addEventListener('horizon-embed-error', handleEmbedError as EventListener);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    try {
      // Remove any existing embed script
      const existingScript = document.querySelector('script[src*="embed.js"]');
      if (existingScript) {
        existingScript.remove();
      }

      // Load the embed script dynamically with the tenant ID
      // Use window.location.origin to load from the same host as the page
      const script = document.createElement('script');
      script.src = `${window.location.origin}/embed.js`;
      script.setAttribute('data-horizon-ref', tenantId || '');

      // Handle script load error
      script.onerror = () => {
        if (redirect) {
          setError(new Error('Failed to load embed script'));
        }
      };

      // Check if embed initialized successfully after a delay
      script.onload = () => {
        // Give the embed script time to initialize (5 seconds)
        errorCheckTimeout.current = setTimeout(() => {
          // Check if there are any error indicators
          // Look for error messages in console or check if embed failed
          const errorElements = document.querySelectorAll('[data-horizon-ep]');
          if (errorElements.length > 0) {
            // Check if embed actually rendered something or if there were errors
            // If after 5 seconds we still see errors in console, it likely failed
            // The console.error interceptor above should have caught it by now
          }
        }, 5000);
      };

      document.body.appendChild(script);
    } catch (err) {
      console.error('Error in script setup:', err);
      if (redirect) {
        setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      }
    }

    return () => {
      // Cleanup: remove script when component unmounts
      const scriptToRemove = document.querySelector('script[src*="embed.js"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }

      // Clear timeout
      if (errorCheckTimeout.current) {
        clearTimeout(errorCheckTimeout.current);
      }

      // Restore original console.error
      if (originalConsoleError.current) {
        console.error = originalConsoleError.current;
      }

      // Remove event listeners
      window.removeEventListener('horizon-embed-error', handleEmbedError as EventListener);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [tenantId, redirect]);

  // If redirect is false, always return null (no error)
  if (!redirect) {
    return null;
  }

  return error;
}

